// Plot boundary templates — common plot shapes with coordinates in meters.
//
// The picker used to live in the v1 entrypoint, which was deleted in #1017
// phase 2. The shapes and their translations survived; the dropdown that
// offered them did not, so TEMPLATES rendered in the context bar and did
// nothing at all (#1581). Same deletion took DXF import's wiring with it.
import { t } from '../shared/i18n.ts';

const TEMPLATE_DATA = [
  { key: "rectangle", points: [{x:0,y:0},{x:20,y:0},{x:20,y:15},{x:0,y:15}] },
  { key: "square", points: [{x:0,y:0},{x:15,y:0},{x:15,y:15},{x:0,y:15}] },
  { key: "l-shape", points: [{x:0,y:0},{x:20,y:0},{x:20,y:10},{x:12,y:10},{x:12,y:18},{x:0,y:18}] },
  { key: "triangle", points: [{x:0,y:0},{x:20,y:0},{x:10,y:15}] },
  { key: "trapezoid", points: [{x:3,y:0},{x:17,y:0},{x:20,y:12},{x:0,y:12}] },
  { key: "u-shape", points: [{x:0,y:0},{x:6,y:0},{x:6,y:10},{x:14,y:10},{x:14,y:0},{x:20,y:0},{x:20,y:18},{x:0,y:18}] },
  { key: "long-narrow", points: [{x:0,y:0},{x:30,y:0},{x:30,y:6},{x:0,y:6}] },
];

// Dynamic getter so names update when language changes
export const PLOT_TEMPLATES = TEMPLATE_DATA.map(tpl => ({
  ...tpl,
  get name() { return t(`templates.${tpl.key}`) || tpl.key; }
}));

/** Wire the TEMPLATES button to a dropdown of plot shapes. Choosing one
 *  replaces the boundary via the planner's applyBoundary entry point, which
 *  saves undo state and re-renders. */
export function initPlotTemplates(): void {
  const btn = document.getElementById('btnTemplates');
  if (!btn || !btn.parentElement) return;
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-expanded', 'false');

  let dropdown: HTMLDivElement | null = null;

  // `refocus`: the menu hangs off <body>, after the whole planner, so once it
  // is gone focus must be put back on the button rather than left to drift.
  const close = (refocus = false) => {
    if (!dropdown) return;
    dropdown.remove();
    dropdown = null;
    btn.setAttribute('aria-expanded', 'false');
    if (refocus) btn.focus();
  };

  btn.addEventListener('click', (event) => {
    event.stopPropagation();
    if (dropdown) {
      close();
      return;
    }

    dropdown = document.createElement('div');
    dropdown.className = 'templates-dropdown';
    dropdown.setAttribute('role', 'menu');

    // Anchor to the viewport and hang off <body>. The context bar scrolls
    // horizontally, so it carries `overflow: auto hidden` and is 33px tall —
    // a dropdown parented to the button gets clipped to that strip and is
    // invisible however high its z-index.
    // dynamic: coordinates are measured from the button at open time
    const anchor = btn.getBoundingClientRect();
    dropdown.style.top = `${Math.round(anchor.bottom + 4)}px`;
    dropdown.style.left = `${Math.round(anchor.left)}px`;

    for (const tpl of PLOT_TEMPLATES) {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'template-item';
      item.setAttribute('role', 'menuitem');
      item.dataset.templateKey = tpl.key;
      item.textContent = tpl.name;
      item.addEventListener('click', () => {
        const planner = (window as unknown as {
          __smartpluvia?: { applyBoundary?: (points: { x: number; y: number }[]) => void };
        }).__smartpluvia;
        planner?.applyBoundary?.(tpl.points);
        close(true);
      });
      dropdown.appendChild(item);
    }

    // A menu takes focus and the arrow keys. Its keys stop here: Space and
    // letters would otherwise also reach the planner's shortcuts behind it.
    dropdown.addEventListener('keydown', (e) => {
      e.stopPropagation();
      const items = Array.from(dropdown?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
      const at = items.indexOf(document.activeElement as HTMLElement);
      const moveTo = (i: number) => items[(i + items.length) % items.length]?.focus();
      switch (e.key) {
        case 'ArrowDown': moveTo(at + 1); break;
        case 'ArrowUp': moveTo(at - 1); break;
        case 'Home': moveTo(0); break;
        case 'End': moveTo(items.length - 1); break;
        case 'Escape': case 'Tab': close(true); break;
        default: return;
      }
      e.preventDefault();
    });

    document.body.appendChild(dropdown);
    btn.setAttribute('aria-expanded', 'true');
    dropdown.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  });

  document.addEventListener('click', (e) => {
    const target = e.target as Node;
    // `contains`, not identity: the click lands on the <span> inside the
    // button, so comparing against the button itself closed the menu on the
    // very click that opened it.
    if (dropdown && !dropdown.contains(target) && !btn.contains(target)) close();
  });

  // Fixed positioning is computed on open, so a resize would strand the menu
  // beside where the button used to be.
  window.addEventListener('resize', () => close());

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}
